import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { ClipboardTick, Microphone2 } from 'iconsax-react'

import MentionInput from '../../../../molecules/Mention'
import EmojiSelect from './EmojiSelect'
import UploadFile from '../../../UploadFile'
import TaskModal from '@SonarCX/components/molecules/ChatModals/TaskModal'

import { AddErrorAlert } from '@SonarCX/slices/alert'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import {
  selectChatControls,
  updateValue
} from '@SonarCX/slices/chatControlsSlice'
import { selectConvo } from '@SonarCX/slices/convo'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useCanSend } from '@SonarCX/providers/CanSendProvider'
import { selectDMs } from '@SonarCX/slices/dms'
import { selectPresence } from '@SonarCX/slices/presence'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useMediaLoaded } from '@SonarCX/providers/MediaLoadedProvider'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useOnNotes } from '@SonarCX/providers/OnNotesProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'

import { Routes } from '@SonarCX/Routes'
import useStyles from './styles'

const TextArea = ({
  type,
  onTypeSet,
  isDirect,
  checkSubmit,
  disabled,
  tags,
  createTask,
  session,
  isClosed,
  updateAllChannels,
  isGroup,
  scrollToBottom,
  canAddNotes,
  SendButton,
  alert,
  startRecording,
  stopRecording,
  recordingBlob,
  isRecording,
  recordingTime,
  clearRecord,
  children
}) => {
  const dispatch = useAppDispatch()

  const { message, file, taskVals } = useAppSelector(selectChatControls)
  const { allConversations } = useAppSelector(selectConvo)
  const { dms } = useAppSelector(selectDMs)
  const { presence } = useAppSelector(selectPresence)
  const { allUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const { doneUploading } = useMediaLoaded()
  const { canSend } = useCanSend()
  const {
    state: editState,
    update: updateEdit,
    end: endEdit
  } = useChatEditState()
  const { onNotes: allOnNotes, /*showNotes,*/ hideNotes } = useOnNotes()
  const hasPermission = useAccess()
  const { isSmall } = useBreakpoint()

  const [users, setUsers] = React.useState({})
  const [names, setNames] = React.useState('')
  const [closed, setClosed] = React.useState(false)
  const [showTaskModal, setShowTaskModal] = React.useState(false)

  const onRecordClick = React.useCallback(() => {
    if (disabled) return
    if (file?.length === 5) {
      dispatch(AddErrorAlert({ err: { message: 'Maximum 5 attachments' } }))
      return
    }
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }, [isRecording, stopRecording, startRecording, file, dispatch, disabled])

  const canChangeChannel = React.useMemo(() => {
    if (isDirect) {
      return hasPermission(PERMS.PeerFull)
    }

    return hasPermission(PERMS.ConversationsMultichannel)
  }, [isDirect, hasPermission])

  React.useEffect(() => {
    if (!canAddNotes) {
      hideNotes(session)
    }
  }, [canAddNotes, hideNotes, session])

  const onNotes = React.useMemo(
    () => Boolean(allOnNotes[session]),
    [allOnNotes, session]
  )

  const conversations = React.useMemo(
    () => (isDirect ? dms : allConversations),
    [isDirect, dms, allConversations]
  )

  const convo = React.useMemo(
    () => conversations[session],
    [conversations, session]
  )

  const isPersonalSpace = React.useMemo(
    () =>
      isDirect && convo?.users?.every(i => currentUser()?.id === i) && !isGroup,
    [currentUser, convo, isDirect, isGroup]
  )

  const userIDs = React.useMemo(() => {
    const convo = conversations[session]

    if (!convo) {
      return []
    }

    if (isDirect) {
      return !convo.user ? convo.users : [convo.user?.id]
    } else {
      return convo.users
    }
  }, [conversations, session, isDirect])

  // Updates closed status
  React.useEffect(() => {
    const convo = conversations[session]

    if (closed || !convo) {
      return
    }

    setClosed(Boolean(convo.closed))
  }, [conversations, session, closed])

  const userNames = React.useMemo(() => {
    let names = []
    const users = [...userIDs]

    // remove self from list
    if (!isDirect) {
      const idx = users.indexOf(currentUser()?.id)
      users.splice(idx, 1)

      // case for 1 client
      if (users.length === 1 && allUsers[users[0]]) {
        names.push(allUsers[users[0]].name)
      } else if (users.length === 2 || users.length === 3) {
        // case for 2 agents and 1 client
        users.forEach(id => {
          if (allUsers[id]) {
            names.push(allUsers[id].name)
          }
        })
      } else {
        return 'group'
      }
    } else {
      if (users.length === 1) {
        const convo = conversations[session]
        return convo.user?.name
      } else if (users.length >= 2) {
        const idx = users.indexOf(currentUser()?.id)
        users.splice(idx, 1)

        if (users.length === 2 || users.length === 3) {
          users.forEach(id => {
            if (allUsers[id]) {
              names.push(allUsers[id].name)
            }
          })
        } else {
          return 'group'
        }
      }
    }

    return names.length > 1 ? names.join(', ') : names
  }, [userIDs, allUsers, currentUser, isDirect, conversations, session])

  // Gets channels from users
  const allChannels = React.useMemo(() => {
    const channels = {}

    Object.keys(users).forEach(userID => {
      // filters out own channels from dropdown
      if (userID === currentUser()?.id) {
        return
      }

      const user = users[userID]

      if (user.channels) {
        user.channels.forEach(channel => {
          channels[channel.id] = { ...channel, _owner: userID, user }
        })
      }
    })

    return channels
  }, [users, currentUser])

  const noAuthUserIDs = React.useMemo(
    () =>
      users
        ? Object.values(users)
            .filter(u => !u.auth)
            .map(u => u.id)
        : [],
    [users]
  )

  const classes = useStyles({
    onMessages: !onNotes,
    disabled: disabled || type === 'Task' || !canChangeChannel,
    banner: names.length !== 0 && !closed && type === 'Live',
    type: !onNotes && type === 'Email',
    SMS: !onNotes && type === 'SMS',
    doneUploading,
    smallMedia: isSmall,
    keepHeight: ['Email', 'Task'].includes(type)
  })

  React.useEffect(() => {
    updateAllChannels(allChannels)
  }, [updateAllChannels, allChannels])

  // Gets users from IDs
  React.useEffect(() => {
    const users = {}

    userIDs.forEach(id => {
      const user = allUsers[id]
      if (user) {
        users[id] = user
      }
    })

    setUsers(users)
  }, [allUsers, userIDs])

  React.useEffect(() => {
    if (type) {
      scrollToBottom()
    }
  }, [type, scrollToBottom])

  React.useEffect(() => {
    if (recordingBlob) {
      dispatch(
        updateValue('file', old =>
          [...(old ?? []), new File([recordingBlob], 'audio-note.wav')].filter(
            f => f
          )
        )
      )
      clearRecord()
    }
  }, [recordingBlob, dispatch, clearRecord])

  // check presence
  React.useEffect(() => {
    let offline = []
    noAuthUserIDs.forEach(id => {
      if (presence[id]) {
        const p = presence[id]
        if (p === 'offline') {
          offline.push(id)
        }
      } else {
        offline.push(id)
      }
    })

    let names = []
    // get names here
    offline.forEach(id => {
      if (allUsers[id]) {
        names.push(allUsers[id]?.name)
      }
    })

    // passing in usernames to msg
    if (names.length) {
      // @ts-ignore
      setNames(names)
    } else {
      // resetting banner
      setNames('')
    }
  }, [noAuthUserIDs, presence, allUsers])

  React.useEffect(() => {
    if (editState.quoting) {
      dispatch(updateValue('message', editState.newMessage))
      endEdit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, editState.quoting, endEdit])

  const handleTextChange = React.useCallback(
    e => {
      const val = e.target.value
      if (type === 'Task') {
        dispatch(updateValue('taskVals', vals => ({ ...vals, content: val })))
      } else {
        dispatch(updateValue('message', val))
      }
    },
    [dispatch, type]
  )

  const handleEmojiSelect = emoji => {
    const val = emoji.native
    if (type === 'Task') {
      dispatch(
        updateValue('taskVals', vals => ({
          ...vals,
          content: vals.content + val
        }))
      )
    } else if (editState.editing) {
      updateEdit(editState.newMessage + val)
    } else {
      dispatch(updateValue('message', message + val))
    }
  }

  const calculatePlaceholder = React.useCallback(() => {
    if (disabled) {
      if (isClosed) {
        if (canAddNotes) {
          return 'Leave a note...'
        }

        return "You don't have permission to add notes"
      }

      return 'Join this conversation to enable replies'
    }

    if (type === 'Task' && !onNotes) {
      return 'Enter task details'
    }

    if (onNotes) {
      return 'Type note...'
    }

    if (isPersonalSpace) {
      return 'Write something down'
    }

    if (type === 'Live') {
      if (isSmall) {
        return 'Type message...'
      }

      return `Type message to ${userNames} ...`
    }

    return `Type your ${type}`
  }, [
    canAddNotes,
    disabled,
    isClosed,
    onNotes,
    type,
    userNames,
    isPersonalSpace,
    isSmall
  ])

  const placeholder = calculatePlaceholder()

  const text = React.useMemo(() => {
    if (type === 'Task') {
      return taskVals.content
    }

    return message.trim().length > 0 ? message : ''
  }, [type, taskVals.content, message])

  const handleTaskClick = React.useCallback(() => {
    setShowTaskModal(true)
  }, [])

  const handleTaskClose = React.useCallback(() => {
    setShowTaskModal(false)
  }, [])

  const handleTaskCreation = React.useCallback(
    task => {
      const taskURL = `${window.location.origin}${Routes.TASKS}/${task.id}`
      dispatch(updateValue('message', msg => `${msg}${taskURL}`))
    },
    [dispatch]
  )

  const onChange = React.useCallback(
    e => {
      editState.editing ? updateEdit(e.target.value) : handleTextChange(e)
    },
    [editState.editing, handleTextChange, updateEdit]
  )

  const disabledSend = React.useMemo(
    () => (onNotes && !canAddNotes) || (!onNotes && (disabled || !canSend)),
    [onNotes, canAddNotes, disabled, canSend]
  )
  return (
    <>
      <div className={clsx(classes.root, disabled && classes.disabled)}>
        <div className={classes.disableArea}>
          <div className={classes.controlContainer}>
            <div className={classes.fields}>
              <MentionInput
                placeholder={
                  editState.editing ? 'Edit last message...' : placeholder
                }
                className={classes.textArea}
                inputClass='text'
                onKeyDown={checkSubmit}
                value={editState.editing ? editState.newMessage : text}
                onChange={onChange}
                disabled={disabledSend}
                tags={tags}
                users={userIDs}
                isP2P={isDirect && !isGroup}
                focus={session}
                type={type}
                autoFocus
                disableTags
              />

              {alert}
            </div>
          </div>
        </div>

        {isRecording && (
          <div className={classes.recordingControls}>
            <div className={classes.recordingStatus}>
              <div className={classes.recordingDot}></div>
              <label>Recording</label>
            </div>
            <label>
              {Math.floor(recordingTime / 60)}:
              {String(recordingTime % 60).padStart(2, '0')}
            </label>
          </div>
        )}

        <div className={classes.sendControls}>
          <div className={classes.controlsBlock}>
            {/* @ts-ignore */}
            <EmojiSelect
              handleEmojiSelect={handleEmojiSelect}
              disabled={disabled}
              onTask={type === 'Task'}
            />

            <UploadFile
              multi
              enablePastingFiles={true}
              onSetFile={f => {
                endEdit()
                dispatch(
                  updateValue('file', old => [...(old ?? []), f].filter(f => f))
                )
                dispatch(
                  updateValue('taskVals', vals => ({ ...vals, file: f }))
                )
              }}
              className={classes.icon}
              fileLength={Boolean(file?.length >= 5)}
              disabled={disabled}
            />

            <Microphone2
              className={classes.icon}
              style={{
                marginLeft: '8px',
                color: isRecording ? 'red' : 'grey'
              }}
              onClick={onRecordClick}
            />

            <ClipboardTick
              className={classes.icon}
              style={{
                marginLeft: '8px',
                color: 'grey'
              }}
              onClick={handleTaskClick}
            />
          </div>

          <div className={classes.children}>{children}</div>

          <SendButton />
        </div>
      </div>

      {showTaskModal && (
        // @ts-ignore
        <TaskModal
          open
          onClose={handleTaskClose}
          onTaskCreated={handleTaskCreation}
          canEdit
          canUpdate
          singleCreate
          viewAfterCreate={false}
        />
      )}
    </>
  )
}

export default TextArea
